import React, { useState, useEffect } from 'react';
import centerService from '../api/centerService';
import authService from '../api/authService';
import './ManagementPages.css';
import './Modal.css';

// Available zones
const ZONES = [
  { value: 'North Delhi', label: 'North Delhi' },
  { value: 'South Delhi', label: 'South Delhi' },
  { value: 'East Delhi', label: 'East Delhi' },
  { value: 'West Delhi', label: 'West Delhi' },
  { value: 'Noida', label: 'Noida' },
  { value: 'Gurugram', label: 'Gurugram' }
];

const CenterManagement = () => {
  const [centers, setCenters] = useState([]);
  const [editingCenter, setEditingCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const initialFormState = {
    name: '', zone: '', address: '', city: '', state: '', pincode: '',
    contact_person: '', contact_phone: '', contact_email: '',
    capacity: '', facilities: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // Role-based security check
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';

  useEffect(() => {
    setLoading(true);
    centerService.getAllCenters()
      .then(res => {
        setCenters(res.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleOpenAddModal = () => {
    setEditingCenter(null);
    setFormData(initialFormState);
    setFieldErrors({});
    setShowModal(true);
  };

  const handleOpenEditModal = (center) => {
    setEditingCenter(center);
    setFormData({
      name: center.name || '',
      zone: center.zone || '',
      address: center.address || '',
      city: center.city || '',
      state: center.state || '',
      pincode: center.pincode || '',
      contact_person: center.contact_person || '',
      contact_phone: center.contact_phone || '',
      contact_email: center.contact_email || '',
      capacity: center.capacity || '',
      facilities: center.facilities || ''
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCenter(null);
    setFormData(initialFormState);
    setFieldErrors({});
  };

  // Parse error response and extract field-level errors
  const parseErrors = (error) => {
    const errors = {};
    const response = error.response?.data;

    if (response?.errors && Array.isArray(response.errors)) {
      response.errors.forEach(err => {
        const field = err.path || err.param || err.field;
        if (field) {
          errors[field] = err.msg || err.message;
        }
      });
    } else if (response?.message) {
      const msg = response.message.toLowerCase();
      if (msg.includes('name')) {
        errors.name = response.message;
      } else if (msg.includes('address')) {
        errors.address = response.message;
      } else if (msg.includes('city')) {
        errors.city = response.message;
      } else if (msg.includes('email')) {
        errors.contact_email = response.message;
      } else if (msg.includes('phone')) {
        errors.contact_phone = response.message;
      } else {
        errors._general = response.message;
      }
    } else {
      errors._general = 'Could not save center. Please check your input.';
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});

    const action = editingCenter
      ? centerService.updateCenter(editingCenter.id, formData)
      : centerService.createCenter(formData);

    action.then(() => {
      centerService.getAllCenters().then(res => setCenters(res.data));
      handleCloseModal();
    }).catch(err => {
      const errors = parseErrors(err);
      setFieldErrors(errors);
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('🗑️ Are you sure you want to delete this center? This action cannot be undone.')) {
      centerService.deleteCenter(id).then(() => {
        centerService.getAllCenters().then(res => setCenters(res.data));
        alert('✅ Center deleted successfully!');
      }).catch(err => {
        alert(`❌ Error deleting center: ${err.response?.data?.message || err.message}`);
      });
    }
  };

  // Calculate stats for compact header
  const totalCenters = centers.length;
  const totalCapacity = centers.reduce((sum, c) => sum + (parseInt(c.capacity) || 0), 0);

  return (
    <div className="management-page">
      {/* Compact Header with inline stats and Add button */}
      <div className="page-header-compact">
        <div className="header-left">
          <h1 className="page-title">
            <span className="page-title-icon">🏢</span> Center Management
          </h1>
          <div className="inline-stats">
            <span className="stat-item">📊 {totalCenters} Centers</span>
            <span className="stat-item">👥 {totalCapacity} Total Capacity</span>
          </div>
        </div>
        {isScheduler && (
          <button className="add-btn-primary" onClick={handleOpenAddModal}>
            + Add Center
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          <span>Loading center data...</span>
        </div>
      ) : (
        <table className="management-table fade-in">
          <thead>
            <tr>
              <th>Name</th>
              <th>Zone</th>
              <th>Address</th>
              <th>City</th>
              <th>Contact Person</th>
              <th>Phone</th>
              <th>Capacity</th>
              {isScheduler && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {centers.map(center => (
              <tr key={center.id}>
                <td>
                  <strong>{center.name}</strong>
                </td>
                <td>
                  {center.zone && (
                    <span className="zone-badge">{center.zone}</span>
                  )}
                  {!center.zone && '-'}
                </td>
                <td>{center.address}</td>
                <td>{center.city}</td>
                <td>{center.contact_person}</td>
                <td>{center.contact_phone}</td>
                <td>
                  <span className="capacity-badge">{center.capacity}</span>
                </td>
                {isScheduler && (
                  <td className="actions-cell">
                    <button
                      onClick={() => handleOpenEditModal(center)}
                      className="action-button edit"
                      title="Edit center"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(center.id)}
                      className="action-button delete"
                      title="Delete center"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal Dialog */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCenter ? '✏️ Edit Center' : '➕ Add New Center'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {fieldErrors._general && (
                <div className="form-error-banner">
                  ⚠️ {fieldErrors._general}
                </div>
              )}
              <div className="form-grid">
                <div className={`form-field ${fieldErrors.name ? 'has-error' : ''}`}>
                  <label>🏢 Center Name</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., CDC 1 Hauzkhas"
                    required
                    className={fieldErrors.name ? 'input-error' : ''}
                  />
                  {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                </div>

                <div className={`form-field ${fieldErrors.zone ? 'has-error' : ''}`}>
                  <label>📍 Zone</label>
                  <select
                    name="zone"
                    value={formData.zone}
                    onChange={handleInputChange}
                    required
                    className={fieldErrors.zone ? 'input-error' : ''}
                  >
                    <option value="">-- Select Zone --</option>
                    {ZONES.map(z => (
                      <option key={z.value} value={z.value}>{z.label}</option>
                    ))}
                  </select>
                  {fieldErrors.zone && <span className="field-error">{fieldErrors.zone}</span>}
                </div>

                <div className={`form-field ${fieldErrors.address ? 'has-error' : ''}`}>
                  <label>📍 Address</label>
                  <input
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    required
                    className={fieldErrors.address ? 'input-error' : ''}
                  />
                  {fieldErrors.address && <span className="field-error">{fieldErrors.address}</span>}
                </div>

                <div className={`form-field ${fieldErrors.city ? 'has-error' : ''}`}>
                  <label>🏙️ City</label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City name"
                    required
                    className={fieldErrors.city ? 'input-error' : ''}
                  />
                  {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
                </div>

                <div className={`form-field ${fieldErrors.state ? 'has-error' : ''}`}>
                  <label>🗺️ State</label>
                  <input
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State name"
                    required
                    className={fieldErrors.state ? 'input-error' : ''}
                  />
                  {fieldErrors.state && <span className="field-error">{fieldErrors.state}</span>}
                </div>

                <div className={`form-field ${fieldErrors.pincode ? 'has-error' : ''}`}>
                  <label>📮 Pincode</label>
                  <input
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit pincode"
                    required
                    className={fieldErrors.pincode ? 'input-error' : ''}
                  />
                  {fieldErrors.pincode && <span className="field-error">{fieldErrors.pincode}</span>}
                </div>

                <div className={`form-field ${fieldErrors.contact_person ? 'has-error' : ''}`}>
                  <label>👤 Contact Person</label>
                  <input
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    placeholder="Contact person name"
                    required
                    className={fieldErrors.contact_person ? 'input-error' : ''}
                  />
                  {fieldErrors.contact_person && <span className="field-error">{fieldErrors.contact_person}</span>}
                </div>

                <div className={`form-field ${fieldErrors.contact_phone ? 'has-error' : ''}`}>
                  <label>📞 Contact Phone</label>
                  <input
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                    required
                    className={fieldErrors.contact_phone ? 'input-error' : ''}
                  />
                  {fieldErrors.contact_phone && <span className="field-error">{fieldErrors.contact_phone}</span>}
                </div>

                <div className={`form-field ${fieldErrors.contact_email ? 'has-error' : ''}`}>
                  <label>📧 Contact Email</label>
                  <input
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    placeholder="Email address"
                    required
                    className={fieldErrors.contact_email ? 'input-error' : ''}
                  />
                  {fieldErrors.contact_email && <span className="field-error">{fieldErrors.contact_email}</span>}
                </div>

                <div className={`form-field ${fieldErrors.capacity ? 'has-error' : ''}`}>
                  <label>👥 Capacity</label>
                  <input
                    name="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    placeholder="Maximum students"
                    required
                    className={fieldErrors.capacity ? 'input-error' : ''}
                  />
                  {fieldErrors.capacity && <span className="field-error">{fieldErrors.capacity}</span>}
                </div>

                <div className="form-field full-width">
                  <label>🏗️ Facilities</label>
                  <textarea
                    name="facilities"
                    value={formData.facilities}
                    onChange={handleInputChange}
                    placeholder="Available facilities (optional)"
                    rows="3"
                  ></textarea>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                  ❌ Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingCenter ? '💾 Save Changes' : '➕ Create Center'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CenterManagement;